/* eslint-disable max-len */
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Injector,
    Input,
    OnInit,
    Optional,
    Output
} from '@angular/core';
import { AbstractControl, NG_VALUE_ACCESSOR, NgControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DropdownValue } from '@Work/Accounts/proprietary/controls/dropdown.component';
import { FormValidationDirective } from '@Shared/Directives/proprietary/form-validation.directive';
import { DesignerElement, DesignerElementPropertyType, DesignerElementType } from '@Core/models/proprietary/page/page.state';
import { AppService } from '@Core/services/proprietary/app.service';
import { DateFormulaType } from '@Core/services/proprietary/date-formula.types';
import { SelectionDialogService } from '@Core/services/proprietary/selection-dialog.service';
import { VINType } from '@Core/services/proprietary/vin.service';
import { ZipCodeService, ZipCodeType } from '@Core/services/proprietary/zipcode.service';
import { SubPanelComponent } from '../sub-panel/sub-panel.component';
import { UXContainerComponent } from '../ux-container/ux-container.component';

//<div>HasFocus={ { hasInputFocus } } </div>

export interface FieldOptions {
    size?: number;
    rows?: number;
    codeValues?: [DropdownValue];
    nullValue?: DropdownValue;
    inputWidth?: string;
    showCode?: string;
    minDate?: string;
    maxDate?: string;
    popupOnFocus?: boolean;
    hideEmpty?: boolean;
    spellcheck?: boolean;
}

@Component({
    standalone: false,
    selector: 'ep-field-inner',
    template: `
    <div
      [class]="'unity-form-group unity-fieldWrapper mb-0 ep-col-span-' + getColumnSpan()"
      [class.radio]="controlType == 'ep-radio-group'"
      [class.text]="
        controlType == 'ep-input' || controlType == 'ep-zip' || containerType == 'frame' || controlType == 'ep-vin'
      "
      [class.date]="
        controlType == 'ep-date-input' ||
        controlType == 'ep-date-formula-input' ||
        controlType == 'ep-date-or-date-formula-input' ||
        controlType == 'ep-time-input'
      "
      [class.select]="
        controlType == 'ep-dropdown' ||
        controlType == 'ep-location-selector' ||
        controlType == 'ep-sub-location-selector'
      "
      [class.checkbox]="controlType == 'ep-checkbox'"
      [class.disabled]="(controlDisabled || containerType == 'frame') && !app.DesignerMode"
      [class.success]="zipLookupData.Zip_Lookup_Status == 'Valid' || vinLookupData.VIN_Lookup_Status == 'Valid'"
      [class.warning]="zipLookupData.Zip_Lookup_Status == 'Invalid' || vinLookupData.VIN_Lookup_Status == 'Invalid'"
      [class.error]="zipLookupData.Zip_Lookup_Status == 'Error' || vinLookupData.VIN_Lookup_Status == 'Error'"
      [style.align-items]="controlType == 'ep-checkbox' && textAlign == 'right' ? 'flex-end' : ''"
      [ngbTooltip]="
        tooltip || getLabel(true) || errorMessage || app.Settings.Debug.ShowRuleService ? fieldTooltipContent : false
      "
      placement="top-left"
      [style.width]="manualWidth"
      [tooltipClass]="errorMessage ? 'ep-tooltip-error' : ''"
      *ngIf="!hidden"
    >
      <ng-template #fieldTooltipContent>
        <div>
          <div>
            <b>{{ tooltip ? tooltip : getLabel(true) }}</b>
            <aurora-icon icon="warning" class="ms-1" *ngIf="errorMessage"></aurora-icon>
          </div>
          <div style="margin-top:2px" *ngIf="errorMessage">{{ errorMessage }}</div>
          <div
            style="color:rgb(199, 196, 196);margin-top:2px"
            *ngIf="app.Settings.Debug.ShowRuleService || app.DesignerMode"
          >
            [{{ formControlPath }}]
            <div *ngIf="codeSet && !(codeSet == '') && !(codeSet == 'NONE')">Code Set: {{ codeSet.toUpperCase() }}</div>
          </div>
        </div>
      </ng-template>
      <label *ngIf="!collapseLabelArea" [class]="getLabel() ? '' : 'invisible'">
        <span *ngIf="isRequired" class="indicator-required">*</span>{{ getLabel() || '&nbsp;' }}</label
      >
      <label *ngIf="collapseLabelArea" style="min-height: 0px"></label>
      <!-- <div class="fieldErrorMessage" *ngIf="errorMessage && hasFocus">{{ errorMessage }}</div> -->
      <ng-container [ngSwitch]="controlType">
        <ep-input
          *ngSwitchCase="'ep-input'"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          [format]="format"
          [size]="options.size"
          [rows]="options.rows"
          [spellcheck]="options.spellcheck"
          style="width: 100%"
        ></ep-input>
        <ep-input
          *ngSwitchCase="'ep-zip'"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          [format]="getZipFormat()"
          [size]="options.size"
          [rows]="options.rows"
          (ngModelChange)="zipInputChanged($event)"
          style="width: 100%"
          [status]="
            zipLookupData.Zip_Lookup_Status == 'InProgress'
              ? 'inprogress'
              : zipLookupData.Zip_Lookup_Status == 'Valid'
              ? 'success'
              : zipLookupData.Zip_Lookup_Status == 'Invalid'
              ? 'warning'
              : zipLookupData.Zip_Lookup_Status == 'Error'
              ? 'error'
              : true
          "
          [ngbTooltip]="
            zipLookupData.Zip_Lookup_Status == 'Valid'
              ? 'Zip is valid'
              : zipLookupData.Zip_Lookup_Status == 'Invalid'
              ? 'Zip not found!'
              : zipLookupData.Zip_Lookup_Status == 'Error'
              ? 'System Error!'
              : false
          "
        ></ep-input>
        <ep-input
          *ngSwitchCase="'ep-vin'"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          [format]="format"
          (ngModelChange)="vinInputChanged($event)"
          style="width: 100%"
          [status]="
            vinLookupData.VIN_Lookup_Status == 'InProgress'
              ? 'inprogress'
              : vinLookupData.VIN_Lookup_Status == 'Valid'
              ? 'success'
              : vinLookupData.VIN_Lookup_Status == 'Invalid'
              ? 'warning'
              : vinLookupData.VIN_Lookup_Status == 'Error'
              ? 'error'
              : true
          "
          [ngbTooltip]="
            vinLookupData.VIN_Lookup_Status == 'Valid'
              ? 'VIN is valid'
              : vinLookupData.VIN_Lookup_Status == 'Invalid'
              ? 'VIN not found!'
              : vinLookupData.VIN_Lookup_Status == 'Error'
              ? 'System Error!'
              : false
          "
        ></ep-input>

        <ep-date-input
          *ngSwitchCase="'ep-date-input'"
          [noLabel]="true"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          (calendarClosed)="forceTouched()"
          [popupOnFocus]="options.popupOnFocus === undefined ? true : options.popupOnFocus"
          [minDate]="options.minDate || null"
          [maxDate]="options.maxDate || null"
          [format]="format"
          style="width: 100%"
        ></ep-date-input>
        <ep-date-formula-input
          *ngSwitchCase="'ep-date-formula-input'"
          [noLabel]="true"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          [format]="dateFormulaFormat"
          style="width: 100%"
        ></ep-date-formula-input>
        <ep-date-or-date-formula-input
          *ngSwitchCase="'ep-date-or-date-formula-input'"
          [noLabel]="true"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          [format]="dateFormulaFormat"
          style="width: 100%"
        ></ep-date-or-date-formula-input>
        <ep-time-input
          *ngSwitchCase="'ep-time-input'"
          [noLabel]="true"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          (calendarClosed)="forceTouched()"
          [popupOnFocus]="options.popupOnFocus === undefined ? true : options.popupOnFocus"
          style="width: 100%"
        ></ep-time-input>
        <ep-dropdown
          *ngSwitchCase="'ep-dropdown'"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          (ngModelChange)="forceTouched()"
          [codeSet]="codeSet"
          [codeValues]="options.codeValues"
          [nullValue]="options.nullValue"
          [showCode]="options.showCode"
          [width]="options.inputWidth"
          [hideEmpty]="options.hideEmpty"
          (dataSelected)="dataSelected.emit($event)"
          [addOptionIfMissing]="options.addOptionIfMissing"
          style="width: 100%"
        ></ep-dropdown>
        <ep-checkbox
          *ngSwitchCase="'ep-checkbox'"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          (ngModelChange)="forceTouched()"
          [format]="checkboxFormat"
          [label]="getLabel(true)"
          [hideLabel]="hideLabel"
          [hasParentWrapper]="false"
        ></ep-checkbox>
        <ep-radio-group
          *ngSwitchCase="'ep-radio-group'"
          [(ngModel)]="value"
          [selectedValue]="radioButtonValue"
          [codeValues]="options.codeValues"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          (ngModelChange)="forceTouched()"
          [align]="textAlign"
          [hasParentWrapper]="true"
        ></ep-radio-group>
        <ep-inline-search
          *ngSwitchCase="'ep-inline-search'"
          [noLabel]="true"
          [(ngModel)]="value"
          (ngModelChange)="forceTouched()"
          (searchValueChange)="searchValueChanged($event)"
          [descFormControl]="descFormControl"
          [formControlPath]="formControlPath"
          [descFormControlPath]="descFormControlPath"
          (dataCleared)="dataCleared.emit()"
          (dataSelected)="dataSelected.emit($event)"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          [options]="options"
          [criteria]="criteria"
          [placeholder]="placeholder"
          [objectType]="searchObjectType"
          [form]="form"
          style="width: 100%"
        ></ep-inline-search>
        <ep-forced-search
          *ngSwitchCase="'ep-forced-search'"
          [noLabel]="true"
          [(ngModel)]="value"
          [formControlPath]="formControlPath"
          [descFormControlPath]="descFormControlPath"
          (dataCleared)="dataCleared.emit()"
          (dataSelected)="dataSelected.emit($event)"
          (searchValueChange)="searchValueChanged($event)"
          [disabled]="controlDisabled"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          [options]="options"
          [criteria]="criteria"
          [placeholder]="placeholder"
          [objectType]="searchObjectType"
          [form]="form"
          style="width: 100%"
        ></ep-forced-search>
        <ep-location-selector
          *ngSwitchCase="'ep-location-selector'"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          [userDefinedMode]="options.userDefinedMode"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          (ngModelChange)="forceTouched()"
          style="width: 100%"
        ></ep-location-selector>
        <ep-sub-location-selector
          *ngSwitchCase="'ep-sub-location-selector'"
          [(ngModel)]="value"
          [disabled]="controlDisabled"
          [userDefinedMode]="options.userDefinedMode"
          (focus)="gotFocus()"
          (blur)="lostFocus()"
          (ngModelChange)="forceTouched()"
          style="width: 100%"
        ></ep-sub-location-selector>
        <div
          *ngSwitchDefault
          [class.fieldContainerText]="containerType == 'text'"
          [class.fieldContainerFrame]="containerType == 'frame'"
          style="width: 100%"
        >
          <ng-content></ng-content>
        </div>
      </ng-container>
      <!-- design mode overlay -->
      <div
        *ngIf="app.DesignerMode"
        class="uxDesignerFieldOverlay"
        [class.uxDesignerHiddenElement]="uxContainerConfigMode && hiddenRuleExists"
        [class.uxDesignerSelectableElement]="uxContainerConfigMode"
        [class.uxDesignerElementSelected]="designerElementCheckboxSelected"
        (click)="uxContainerConfigMode ? selectDesignerElement() : null"
      >
        <sm-checkbox
          classes="uxDesignerElementCheckbox"
          [(inputModel)]="designerElementCheckboxSelected"
          (click)="$event.stopPropagation()"
        ></sm-checkbox>

        <aurora-icon icon="eye-slash" class="uxDesignerElementHiddenIndicator" *ngIf="uxContainerConfigMode && hiddenRuleExists"></aurora-icon>
      </div>
    </div>
  `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FieldInnerComponent),
            multi: true
        }
    ]
})
export class FieldInnerComponent implements OnInit {
    @Input()
    label: string = null;

    @Input()
    tooltip: string = null;

    @Input()
    hideLabel: boolean = false;

    @Input()
    collapseLabelArea: boolean = false;

    @Input()
    form: any = null;

    @Input()
    columnSpan: string = '1';

    @Input()
    formControlPath: any = null;

    @Input('format')
    set loadFormat(value: string) {
        this.format = value;
        if (value) {
            let token: any[] = value.toLowerCase().split('-');
            let tokenRaw: any[] = value.split('-');

            switch (token[0]) {
                case 'date':
                case 'datetime':
                    this.controlType = 'ep-date-input';
                    break;

                case 'dateformula':
                case 'termsformula':
                    this.controlType = 'ep-date-formula-input';
                    if (token[0] == 'termsformula') {
                        this.dateFormulaFormat = DateFormulaType.Terms;
                    } else {
                        this.dateFormulaFormat = DateFormulaType.Date;
                    }
                    break;

                case 'dateordateformula':
                    this.controlType = 'ep-date-or-date-formula-input';
                    this.dateFormulaFormat = DateFormulaType.Date;
                    break;

                case 'time':
                    this.controlType = 'ep-time-input';
                    break;

                case 'dropdownselector': //dropdownselector-state, dropdownselector
                    this.controlType = 'ep-dropdown';
                    if (token.length > 1 && token[1] != 'left' && token[1] != 'right' && token[1] != 'center') {
                        this.codeSet = token[1];
                    }
                    break;
                case 'locationselector': //locationselector
                    this.controlType = 'ep-location-selector';
                    break;
                case 'sublocationselector':
                    this.controlType = 'ep-sub-location-selector';
                    break;
                case 'checkbox': //checkbox, checkbox-yn
                    this.controlType = 'ep-checkbox';
                    if (token.length > 1 && token[1] != 'left' && token[1] != 'right' && token[1] != 'center') {
                        this.checkboxFormat = token[1];
                    }
                    break;
                case 'radiogroup': //radiobutton   with options.codeValues
                    this.controlType = 'ep-radio-group';
                    break;
                case 'search':
                    this.controlType = 'ep-inline-search';
                    if (token.length > 1 && token[1] != 'left' && token[1] != 'right' && token[1] != 'center') {
                        this.searchObjectType = token[1];
                    }
                    break;
                case 'forcedsearch':
                    this.controlType = 'ep-forced-search';
                    this.searchObjectType = token[1];
                    break;
                case 'spacer':
                    this.controlType = 'spacer';
                    this.spacer = true;
                    break;
                case 'container':
                    this.controlType = 'container';
                    if (token.length > 1) {
                        this.containerType = token[1];
                    }
                    break;
                case 'zip':
                    this.controlType = 'ep-zip';
                    break;
                case 'vin':
                    this.controlType = 'ep-vin';
                    break;
                default:
                    this.controlType = 'ep-input';
            }

            //override default text alignment
            if (token.indexOf('left') >= 0) {
                this.textAlign = 'left';
            }
            if (token.indexOf('right') >= 0) {
                this.textAlign = 'right';
            }
            if (token.indexOf('center') >= 0) {
                this.textAlign = 'center';
            }
        }
    }

    @Input('disabled')
    controlDisabled: boolean = false;

    @Input()
    options: any = '{}';

    @Input()
    name: any = {};

    @Input()
    criteria: any = {}; //used by inline search

    @Input()
    placeholder: string = ''; //used by inline search

    @Input()
    descFormControlName: string = null; //used by inline search   // LEGACY CONTROL SUPPORT -- ONLY IF USED OUTSIDE OF ep-field

    @Input()
    descFormControlPath: string = null; //used by inline search

    @Output()
    dataSelected = new EventEmitter(); //used by inline search

    @Output()
    dataCleared = new EventEmitter(); //used by inline search

    @Output()
    vinLookup = new EventEmitter(); //used for VIN lookup

    @Input()
    vinLookupData: VINType = {}; //used by VIN lookup

    set designerElementCheckboxSelected(on: boolean) {
        if (on) {
            this.selectDesignerElement('checkbox.click');
        } else {
            this.app.designerSelectElement(this.formControlPath, null, false);
        }
    }

    get designerElementCheckboxSelected(): boolean {
        return this.app.designerElementIsSelected(this.formControlPath);
    }

    get manualWidth(): string {
        let columnSpan = this.getColumnSpan() || '';
        if (columnSpan.toLowerCase().endsWith('px')) {
            return columnSpan;
        } else {
            return null;
        }
    }

    formControl: UntypedFormControl;
    hasFocus: boolean = false;
    innerValue: any = null;

    format: string = null;
    textAlign: string = 'left';
    controlType: string = null;
    checkboxFormat: string = 'TF';
    radioButtonValue: string = null;
    codeSet: string = 'NONE';
    searchObjectType: string = 'Entity';
    spacer: boolean = false; //indicates that there is no edit field contained with this ep-field
    descFormControl: AbstractControl;
    containerType = 'raw';
    dateFormulaFormat: DateFormulaType = DateFormulaType.Date;

    private _formGroup: UntypedFormGroup;

    zipLookupData: ZipCodeType = {};
    zipCodeService: ZipCodeService;
    zipChanged: boolean = false;
    vinChanged: boolean = false;

    gotFocus() {
        this.hasFocus = true;
    }

    lostFocus() {
        this.hasFocus = false;
        this.onTouchedCallback(); //set touched when losing focus
        if (this.controlType == 'ep-zip' && this.zipChanged) {
            this.zipLookup();
        }
        if (
            this.controlType == 'ep-vin' &&
            this.vinChanged &&
            this.app.getGlobalOptionValue('Module_List').includes('VINVerify')
        ) {
            this.emitVinLookup();
        }
    }

    getZipFormat() {
        let countryCode = '';
        if (this.formGroup) {
            let zipFieldName = this.formControlName;
            let prefix = zipFieldName.replace('Zip', ''); //e.g. in CDAA_Vehicle the names are Registration_Zip, Registration_City, etc
            countryCode = this.formGroup.getRawValue()[prefix + 'Country'];
            if (countryCode?.length > 0) {
                countryCode = '-' + countryCode;
            } else {
                countryCode = '';
            }
        }
        return 'zip' + countryCode;
    }

    forceTouched() {
        this.onTouchedCallback();
    }

    get formControlName(): string {
        if (!this.formControlPath) {
            return null;
        }
        return this.formControlPath.split('.').slice(-1)[0]; // last value of formControlPath is the fieldname
    }

    get formGroup(): UntypedFormGroup {
        if (!this.form) {
            return null;
        }
        if (!this._formGroup) {
            // only pay the price to lookup the first time
            let ngControl: NgControl = this.injector.get(NgControl);
            this._formGroup = <UntypedFormGroup>ngControl.control.parent;
        }
        return this._formGroup;
    }

    getLabel(ignoreHide = false) {
        if (!ignoreHide && (this.hideLabel || this.controlType == 'ep-checkbox')) {
            return '';
        }
        let labelOverride: string = null;
        if (this.formValidationDirective) {
            labelOverride = this.formValidationDirective.propertyValue(
                this.formControlPath,
                DesignerElementPropertyType.label
            );
        }
        return labelOverride || this.label;
    }

    getColumnSpan() {
        let columnSpanOverride: string = null;
        if (this.formValidationDirective) {
            columnSpanOverride = this.formValidationDirective.propertyValue(
                this.formControlPath,
                DesignerElementPropertyType.columnSpan
            );
        }
        return columnSpanOverride || this.columnSpan;
    }

    //TODO:!!!!  will this be OK with change detection???   Refactor to a more efficient way?????
    get errorMessage(): string {
        if (this.spacer || !this.form) {
            return '';
        }
        this.lookupFormControl();
        if (this.formControl) {
            if (this.formControl.touched && this.formControl.invalid) {
                return this.buildErrorMessage(this.formControl);
            } else {
                return null;
            }
        } else {
            return 'formControlPath is invalid!';
        }
    }

    get isRequired(): boolean {
        if (this.spacer) {
            return false;
        }
        this.lookupFormControl();
        if (this.formControl) {
            if (this.formControl.invalid && this.formControl.errors && this.formControl.errors.required) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    lookupFormControl() {
        if (this.form && !this.formControl) {
            this.formControl = this.form.get(this.formControlPath);
        }
    }

    zipLookup() {
        if (!this.zipCodeService) {
            this.zipCodeService = this.injector.get(ZipCodeService);
        }
        let zip = '';
        let zipFieldName = this.formControlName;
        let prefix = zipFieldName.replace('Zip', ''); //e.g. in CDAA_Vehicle the names are Registration_Zip, Registration_City, etc
        let country = this.formGroup.getRawValue()[prefix + 'Country'];

        if (country?.toUpperCase() == 'USA') {
            //use first 5 digits for USA
            zip = (this.innerValue || '').substr(0, 5);
        } else if (country?.toUpperCase() == 'CAN') {
            //Strip out hypen and use 6 digits for CAN
            zip = (this.innerValue || '').replace('-', '').substr(0, 6);
        } else {
            zip = this.innerValue || '';
        }

        this.zipLookupData.Zip_Lookup_Status = 'InProgress';

        this.zipCodeService.lookupZip(zip, (zipData: ZipCodeType) => {
            this.zipLookupData = zipData;

            if (zipData && zipData.Zip_Lookup_Status == 'Valid') {
                // if zip is found then default in city, county, state, and country

                if (this.zipLookupData.hasOwnProperty('Alt_Cities')) {
                    this.zipLookupData.Alt_Cities.City.splice(0, 0, this.zipLookupData.City); //Insert preferred city at 0 index
                    this.selectionDialog
                        .selectCity(this.zipLookupData.Alt_Cities.City, this.zipLookupData.County, this.zipLookupData.State)
                        .then((selectedCity) => {
                            if (selectedCity) {
                                this.formGroup.patchValue(
                                    {
                                        [prefix + 'City']: selectedCity,
                                        [prefix + 'County']: zipData.County,
                                        [prefix + 'State']: zipData.State,
                                        [prefix + 'Country']: zipData.Country
                                    },
                                    { emitEvent: true }
                                );
                            }
                        })
                        .catch(() => { });
                } else {
                    this.formGroup.patchValue(
                        {
                            [prefix + 'City']: zipData.City,
                            [prefix + 'County']: zipData.County,
                            [prefix + 'State']: zipData.State,
                            [prefix + 'Country']: zipData.Country
                        },
                        { emitEvent: true }
                    );
                }
            }
            this.cdref.detectChanges();
            this.zipChanged = false;
        });
    }

    zipInputChanged(event: any) {
        this.zipLookupData = {}; //reset so that icon disappears as soon as they type in the field
        this.zipChanged = true;
    }

    emitVinLookup() {
        let vin = this.innerValue || '';

        if (vin) {
            this.vinLookup.emit(true);
            this.vinChanged = false;
        }
    }

    vinInputChanged(event: any) {
        // Reset so the icon disappears as soon as they type in the field, unless its in progress
        this.vinLookupData = {};
        this.vinChanged = true;
    }

    constructor(
        private injector: Injector,
        protected app: AppService,
        private selectionDialog: SelectionDialogService,
        private cdref: ChangeDetectorRef,
        @Optional() private formValidationDirective: FormValidationDirective,
        @Optional() private uxContainerComponent: UXContainerComponent,
        @Optional() private subPanelComponent: SubPanelComponent
    ) { }

    ngOnInit() {
        if (!this.format && !this.spacer) {
            console.error("ep-field-inner: 'format' attribute not specified for " + this.formControlPath);
        }

        if (
            this.descFormControlName &&
            this.descFormControlName > '' &&
            this.descFormControlPath &&
            this.descFormControlPath > ''
        ) {
            console.error(
                `ep-field-inner: Only specify 'descFormControlPath' OR 'descFormControlName'.  descFormControlPath is preferred.  (name=${this.descFormControl}, path=${this.descFormControlPath})`
            );
        }

        //get the description control name
        if (
            (this.descFormControlName && this.descFormControlName > '') ||
            (this.descFormControlPath && this.descFormControlPath > '')
        ) {
            if (this.form) {
                if (this.descFormControlPath && this.descFormControlPath > '') {
                    this.descFormControl = this.form.get(this.descFormControlPath);
                    if (!this.descFormControl) {
                        console.error('No form control found for descFormControlPath.  (Path=' + this.descFormControlPath + ')');
                    }
                } else {
                    //legacy lookup by descFormControlName.
                    //TODO:  This block should be able to be removed after all ep-field-inner instances wrapped by ep-field
                    let ngControl: NgControl = this.injector.get(NgControl);
                    let controlPathArray = ngControl.path; //path to direct parent of this control (in array form)
                    controlPathArray = controlPathArray.slice(0, controlPathArray.length - 1); //remove the ID field name from path
                    let formGroupPath: string = null;
                    controlPathArray.forEach(function (segment) {
                        if (segment) {
                            formGroupPath = !formGroupPath ? segment : formGroupPath + '.' + segment;
                        }
                    });
                    let controlPath = formGroupPath + '.' + this.descFormControlName; //add the desc field name to the path
                    this.descFormControl = this.form.get(controlPath);
                    if (!this.descFormControl) {
                        console.error('No form control found for descFormControlName.  (Path=' + controlPath + ')');
                    }
                }
            } else {
                console.error(
                    "'form' attribute must be specified if 'descFormControlName' or 'descFormControlPath' attribute is used."
                );
            }
        }
    }

    getErrors(path: string): any {
        let control = this.form.get(path);
        if (control) {
            return control.errors;
        } else {
            return {};
        }
    }

    getFormControl(path: string) {
        this.form.get(path);
    }

    buildErrorMessage(control: UntypedFormControl): string {
        let messages: string = null;
        Object.keys(control.errors).forEach((keyError) => {
            let errText = '';
            switch (keyError) {
                case 'required':
                case 'requiredIf':
                    let requiredIfMessage = control.errors[keyError].message;
                    let conditional = requiredIfMessage ? true : false;
                    if (requiredIfMessage == 'NO-MESSAGE') {
                        requiredIfMessage = '';
                    }
                    if (
                        //prevent double message if manual message on rule looks like standard require message.
                        requiredIfMessage &&
                        requiredIfMessage.toLowerCase().startsWith(this.label.toLowerCase() + ' is required')
                    ) {
                        requiredIfMessage = '';
                    }
                    if (conditional) {
                        errText = `${this.label} is required! ${requiredIfMessage}`;
                    } else {
                        errText = `${this.label} is required`;
                    }
                    break;
                case 'pattern':
                    errText = `${this.label} has wrong pattern!`;
                    break;
                case 'email':
                    errText = `${this.label} has wrong email format!`;
                    break;
                case 'minlength':
                    errText = `${this.label} is too short! ${control.errors[keyError].requiredLength} character minimum.`;
                    break;
                case 'maxlength':
                    errText = `${this.label} is too long! ${control.errors[keyError].requiredLength} character max.`;
                    break;
                case 'range':
                    errText = `${this.label} must be between ${control.errors[keyError].min} and ${control.errors[keyError].max}!`;
                    break;
                case 'validIf':
                    let validIfMessage = control.errors[keyError].message || '';
                    if (validIfMessage == 'NO-MESSAGE') {
                        validIfMessage = '';
                    }
                    errText = `${this.label} is invalid! ${validIfMessage}`;
                    break;
                default:
                    errText = `${this.label}: ${keyError}: ${control.errors[keyError]}`;
            }
            if (!messages) {
                messages = errText;
            } else {
                messages = messages + '  ' + errText;
            }
        });
        return messages;
    }

    logForm() {
        console.log(this.form);
    }

    searchValueChanged(searchModel: any) {
        if (this.descFormControlPath) {
            let descControl: UntypedFormControl = this.form.get(this.descFormControlPath);
            if (descControl) {
                descControl.setValue(searchModel.Desc, { emitEvent: false });
            }
        }

        if (this.formControlPath) {
            let control: UntypedFormControl = this.form.get(this.formControlPath);
            if (control) {
                control.setValue(searchModel.Value, { emitEvent: true });
            }
        }
    }

    selectDesignerElement(mode: string = '') {
        if (this.uxContainerComponent) {
            if (mode != 'checkbox.click') {
                this.app.designerClearElementSelection();
            }

            let objectNodeType: string = null;
            let formGroup: string = null;
            let fieldPath: string = null;

            if (this.formControlPath) {
                let pos = this.formControlPath.indexOf('.');
                if (pos > 0) {
                    formGroup = this.formControlPath.substr(0, pos);
                    fieldPath = this.formControlPath.substr(pos + 1);
                } else {
                    fieldPath = this.formControlPath;
                }
            }

            if (this.uxContainerComponent.formGroupMapping) {
                if (this.formGroup) {
                    let mapping = this.uxContainerComponent.formGroupMapping.find((mapping) => mapping.formGroup == formGroup);
                    if (mapping) {
                        objectNodeType = mapping.nodeType;
                    }
                }
            }

            let designerElement: DesignerElement = {
                type: DesignerElementType.Field,
                name: this.name,
                label: this.label,
                format: this.format,
                columnSpan: this.columnSpan,
                formGroup: formGroup,
                fieldPath: fieldPath, //do not include the formgroup as part of the field path
                objectNodeType: objectNodeType,
                evaluatedFormRules: this.formValidationDirective ? this.formValidationDirective.evaluatedFormRules : null
            };

            this.uxContainerComponent.currentDesignerElement = designerElement;
            this.app.designerSelectElement(this.formControlPath, designerElement, true);
        }
    }

    get moveable(): boolean {
        //field can be moved
        if (this.app.DesignerMode && this.uxContainerComponent && this.uxContainerComponent.uxConfigMode) {
            return true;
        }
        return false;
    }

    get uxContainerConfigMode(): boolean {
        if (this.app.DesignerMode && this.uxContainerComponent && this.uxContainerComponent.uxConfigMode) {
            //verify that this field belongs to the same subpanel (if one exists).  
            //  If not, then it is not really part of the uxContainer (actually is part of a child edit panel)
            if (
                this.uxContainerComponent.subPanelComponent &&
                this.subPanelComponent &&
                this.uxContainerComponent.subPanelComponent != this.subPanelComponent
            ) {
                return false;
            }

            return true;
        }
        return false;
    }

    //***** BEGIN CONTROL VALUE ACCESSOR HANDLERS *****//

    //Placeholders for the callbacks which are later provided by the Control Value Accessor
    private onChangeCallback = (_: any) => { };
    private onTouchedCallback = () => { };

    //get accessor
    get value(): any {
        return this.innerValue;
    }

    //set accessor including call the onchange callback
    set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.onChangeCallback(v);
        }
    }

    //From ControlValueAccessor interface - Allows Angular to update the model
    writeValue(v: string): void {
        if (v !== this.innerValue) {
            this.innerValue = v;
            ////this.onChangeCallback(v);
        }
    }

    //From ControlValueAccessor interface - Allows Angular to register a function to call when the model changes.
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to register a function to call when the input has been touched.
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.controlDisabled = isDisabled;
    }

    get hidden() {
        if (this.app.DesignerMode && this.app.DesignerModeShowHidden) {
            return false;
        }
        if (this.formValidationDirective) {
            return this.formValidationDirective.hidden(this.formControlPath);
        }
        return false;
    }

    get hiddenRuleExists() {
        if (this.formValidationDirective) {
            return this.formValidationDirective.hidden(this.formControlPath);
        }
        return false;
    }

    //***** END CONTROL VALUE ACCESSOR HANDLERS *****//
}
